import { useRouter, useSearchParams } from "next/navigation";
import { useCallback, useMemo } from "react";

interface UseSearchParamFilterOptions<T> {
  encode(value: T): string | null;
  decode(value: string | null): T;
  scroll?: boolean;
}

export function useSearchParamFilter<T = string | null>(
  key: string,
  options: UseSearchParamFilterOptions<T>,
): [T | null, (v: T) => void] {
  const router = useRouter();
  const searchParams = useSearchParams();

  const { scroll = false } = options;

  const encode = options?.encode;
  const decode = options?.decode;

  const decodedValue = useMemo(() => {
    return searchParams ? decode(searchParams.get(key)) : null;
  }, [key, searchParams, decode]);

  const setValue = useCallback(
    (v: T) => {
      const nextUrl = new URL(window.location.href);
      const encodedValue = encode(v);
      if (encodedValue === null) {
        nextUrl.searchParams.delete(key);
      } else {
        nextUrl.searchParams.set(key, encodedValue);
      }
      router.replace(nextUrl.href, { scroll });
    },
    [key, router, encode, scroll],
  );
  return [decodedValue, setValue];
}

function encodeArray(value: string[]): string | null {
  if (value.length === 0) return null;
  return value.join(",");
}

function decodeArray(value: string): string[] | null {
  if (!value) return null;
  if (value.includes(",")) {
    return value.split(",");
  }
  return [value];
}

export function useScalarSearchParamFilter<T = string | null>(
  key: string,
  defaultValue: T,
): [T | null, (v: T) => void] {
  const [value, setValue] = useSearchParamFilter<T>(key, {
    encode: (v) => (v === defaultValue ? null : String(v)),
    decode: (v) => (v === null ? defaultValue : (v as unknown as T)),
  });
  return [value, setValue];
}

export function useArraySearchParamFilter(key: string): [string[], (v: string[]) => void] {
  const [value, setValue] = useSearchParamFilter<string[] | null>(key, {
    encode: encodeArray,
    decode: decodeArray,
  });
  return [value ?? [], setValue];
}

export function useBooleanSearchParamFilter(
  key: string,
  _defaultValue = false,
): [boolean | null, (v: boolean) => void] {
  const [value, setValue] = useSearchParamFilter<boolean>(key, {
    encode: (v) => (v ? "true" : "false"),
    decode: (v) => v === "true",
  });
  return [value, setValue];
}

export function useNumberSearchParamFilter(
  key: string,
  maxNumber?: number,
): [number | null, (v: number) => void] {
  const [value, setValue] = useSearchParamFilter<number>(key, {
    encode: (v) => String(v),
    decode: (v) => Number(v),
  });
  if (maxNumber && value && value > maxNumber) {
    setValue(maxNumber);
  }
  return [value, setValue];
}
